import { EventType } from "@/analytics/analytics-events";
import type { DiscardManualBlursProperties } from "@/analytics/manual-blurring-events-properties";
import { EventBarrier } from "@/components/common/event-barrier";
import { OverlayToolbar } from "@/components/ui/overlay-toolbar";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { deactivateTool, ToolName } from "@/store/ui/ui-slice";
import {
  Checkmark3Icon,
  CloseIcon,
  FaroText,
  HistoryRedoIcon,
  HistoryUndoIcon,
  ToolButton,
  ToolGroup,
  TranslateVar,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import Stack from "@mui/material/Stack";
import { useCallback, type JSX } from "react";
import {
  useManualBlurringUndoRedo,
  useNumPanosBlurred,
  useResetManualBlurringStore,
} from "./manual-blurring-store";
import { useApplyManualBlurring } from "./use-apply-manual-blurring";

/** @returns Overlay for the manual blurring mode */
export function ManualBlurringToolbar(): JSX.Element | null {
  const dispatch = useAppDispatch();

  const activeTool = useAppSelector(selectActiveTool);

  const { undo, redo, pastStates, futureStates } = useManualBlurringUndoRedo();

  const resetManualBlurringStore = useResetManualBlurringStore();
  const applyManualBlurring = useApplyManualBlurring();

  const onCancel = useCallback(() => {
    Analytics.track<DiscardManualBlursProperties>(
      EventType.discardManualBlurs,
      { via: "toolbar" },
    );

    resetManualBlurringStore();

    dispatch(deactivateTool());
  }, [dispatch, resetManualBlurringStore]);

  const onUndo = useCallback(() => {
    Analytics.track(EventType.undoManualBlur);

    undo(1);
  }, [undo]);

  const onRedo = useCallback(() => {
    Analytics.track(EventType.redoManualBlur);

    redo(1);
  }, [redo]);

  const numPanosBlurred = useNumPanosBlurred();

  if (activeTool !== ToolName.manualBlurring) return null;

  return (
    <EventBarrier>
      <Stack
        aria-label="manual blurring toolbar"
        direction="column"
        sx={{ justifyContent: "flex-end" }}
      >
        <OverlayToolbar direction="row" sx={{ alignItems: "center" }}>
          {numPanosBlurred > 0 && (
            <ToolGroup sx={{ px: 2 }}>
              <FaroText variant="bodyM" dark>
                <TranslateVar name="numPanos">{numPanosBlurred}</TranslateVar>{" "}
                pano{numPanosBlurred > 1 ? "s" : ""} blurred
              </FaroText>
            </ToolGroup>
          )}
          <ToolGroup sx={{ px: 1, flexDirection: "row", gap: 1 }}>
            <ToolButton
              aria-label="undo manual blur"
              disabled={pastStates.length === 0}
              onClick={onUndo}
            >
              <HistoryUndoIcon />
            </ToolButton>
            <ToolButton
              aria-label="redo manual blur"
              disabled={futureStates.length === 0}
              onClick={onRedo}
            >
              <HistoryRedoIcon />
            </ToolButton>
          </ToolGroup>

          <ToolGroup sx={{ px: 1, flexDirection: "row", gap: 1 }}>
            <ToolButton aria-label="cancel manual blur" onClick={onCancel}>
              <CloseIcon />
            </ToolButton>

            <ToolButton
              aria-label="apply manual blur"
              onClick={() => applyManualBlurring(false, "toolbar")}
            >
              <Checkmark3Icon color="primary" />
            </ToolButton>
          </ToolGroup>
        </OverlayToolbar>
      </Stack>
    </EventBarrier>
  );
}
